import { updatePreset, updateSurfacePalette } from '@primeuix/themes';
import { computed, reactive, ref, watch } from 'vue';
import { isCorporateClient } from '@/composables/useEdverumClient.js';
import UserSetting from '@/models/UserSetting.js';
import { DEFAULT_PRIMARY, getPresetExt } from '../theme/ColorThemes.js';

const isDarkModeEnabled = computed(() => UserSetting.findByKey('dark_mode')?.value ?? false);
const getPrimaryColor = computed(() => UserSetting.findByKey('primary_color')?.value ?? DEFAULT_PRIMARY);

const layoutConfig = reactive({
    preset: 'Aura',
    primary: DEFAULT_PRIMARY,
    surface: null,
    darkTheme: false,
    menuMode: 'static',
    layoutTheme: 'colorScheme',
});

const layoutState = reactive({
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false,
    rightMenuActive: false,
    topbarMenuActive: false,
    sidebarActive: false,
    activeMenuItem: null,
    overlaySubmenuActive: false,
    // Corporate clients default to an expanded sidebar so the static
    // "Manage Course" / "Manage Users" menu is immediately discoverable.
    // Stanbridge keeps the summary (collapsed) default. Users can still
    // toggle either way via the topbar control; this only sets the
    // initial per-session state at app boot.
    sidebarSummaryMode: !isCorporateClient(),
    topbarVisible: true,
});
const tabs = ref([]);

// Watch for changes to dark mode setting and apply them
// Using immediate: true ensures it applies on initial load once user settings are loaded
watch(
    isDarkModeEnabled,
    newValue => {
        layoutConfig.darkTheme = newValue;

        if (newValue) {
            document.documentElement.classList.add('app-dark');
        } else {
            document.documentElement.classList.remove('app-dark');
        }
    },
    { immediate: true }
);

watch(
    getPrimaryColor,
    newValue => {
        layoutConfig.primary = newValue;
        applyTheme('primary', newValue);
    },
    { immediate: true }
);

function applyTheme(type, color) {
    if (type === 'primary') {
        updatePreset(getPresetExt(color));
    } else if (type === 'surface') {
        updateSurfacePalette(color.palette);
    }
}

export function useLayout() {
    const setActiveMenuItem = item => {
        layoutState.activeMenuItem = item.value || item;
    };

    const toggleMenu = () => {
        if (layoutConfig.menuMode === 'overlay') {
            layoutState.overlayMenuActive = !layoutState.overlayMenuActive;
        }

        if (window.innerWidth >= 1200) {
            layoutState.staticMenuDesktopInactive = !layoutState.staticMenuDesktopInactive;
        } else {
            layoutState.staticMenuMobileActive = !layoutState.staticMenuMobileActive;
        }
    };
    const closeMenu = () => {
        if (layoutConfig.menuMode === 'overlay') {
            layoutState.overlayMenuActive = false;
        }

        if (window.innerWidth >= 1200) {
            layoutState.staticMenuDesktopInactive = true;
        } else {
            layoutState.staticMenuMobileActive = false;
        }
    };

    const toggleConfigSidebar = () => {
        if (isSidebarActive.value) {
            layoutState.overlayMenuActive = false;
            layoutState.overlaySubmenuActive = false;
            layoutState.staticMenuMobileActive = false;
            layoutState.menuHoverActive = false;
        }
        layoutState.configSidebarVisible = !layoutState.configSidebarVisible;
    };

    const toggleSidebarSummary = (item = undefined) => {
        layoutState.sidebarSummaryMode = !layoutState.sidebarSummaryMode;
        return item;
    };

    const openTab = value => {
        tabs.value = [...tabs.value, value];
    };
    const closeTab = index => {
        tabs.value.splice(index, 1);
        tabs.value = [...tabs.value];
    };

    const showTopbar = () => {
        layoutState.topbarVisible = true;
    };

    const hideTopbar = () => {
        layoutState.topbarVisible = false;
    };

    const isTopbarVisible = computed(() => layoutState.topbarVisible);

    const isSidebarActive = computed(
        () => layoutState.overlayMenuActive || layoutState.staticMenuMobileActive || layoutState.overlaySubmenuActive
    );

    const isDesktop = computed(() => window.innerWidth >= 1200);

    const isSlim = computed(() => layoutConfig.menuMode === 'slim');
    const isSlimPlus = computed(() => layoutConfig.menuMode === 'slim-plus');
    const isHorizontal = computed(() => layoutConfig.menuMode === 'horizontal');
    const isOverlay = computed(() => layoutConfig.menuMode === 'overlay');

    const isDarkTheme = computed(() => layoutConfig.darkTheme);
    const getPrimary = computed(() => layoutConfig.primary);
    const getSurface = computed(() => layoutConfig.surface);

    return {
        layoutConfig,
        layoutState,
        getPrimary,
        getSurface,
        isDarkTheme,
        toggleMenu,
        closeMenu,
        toggleSidebarSummary,
        isSidebarActive,
        setActiveMenuItem,
        toggleConfigSidebar,
        isSlim,
        isSlimPlus,
        isHorizontal,
        isDesktop,
        isOverlay,
        applyTheme,
        openTab,
        tabs,
        closeTab,
        isTopbarVisible,
        showTopbar,
        hideTopbar,
    };
}
